import { forwardRef, type MouseEventHandler } from "react";

import { TooltipIconButton } from "../IconButton";
import { ScreenFullIcon, ScreenNormalIcon } from "../../icons/glyphs";
import { MEDIA_FULLSCREEN_BUTTON_ATTR } from "./mediaFullscreenButtonReveal";

export interface MediaFullscreenButtonProps {
  /** 현재 전체화면(확대) 상태 — true면 복귀 아이콘/라벨로 스왑. Default false. */
  fullscreenActive?: boolean;
  /** 진입 라벨(tooltip이 곧 접근성 이름). Default "Open fullscreen". */
  enterLabel?: string;
  /** 복귀 라벨. Default "Exit fullscreen". */
  exitLabel?: string;
  disabled?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
}

/**
 * 미디어 프레임 우상단 액션용 전체화면 토글 버튼 프리셋. ZoomPanWrapper의 reset 버튼과
 * 같은 조립(IconButton sm/surface + 아래쪽 tooltip = 접근성 이름)이라 한 row에서 시각이
 * 일치한다. 전체화면 열림 상태와 오버레이(MediaOverlay)는 여전히 consumer 소유 — 이 버튼은
 * 모습·아이콘/라벨 스왑만 표준화한다.
 *
 * ref는 native `<button>`으로 forward된다 — overlay가 원래 player를 unmount하는 소비처가
 * 닫힘 후 새로 마운트된 버튼으로 포커스를 복원하는 경로.
 */
export const MediaFullscreenButton = forwardRef<HTMLButtonElement, MediaFullscreenButtonProps>(
  (
    {
      fullscreenActive = false,
      enterLabel = "Open fullscreen",
      exitLabel = "Exit fullscreen",
      disabled,
      onClick,
    },
    ref,
  ) => (
    <TooltipIconButton
      ref={ref}
      {...{ [MEDIA_FULLSCREEN_BUTTON_ATTR]: "" }}
      size="sm"
      variant="surface"
      tooltip={fullscreenActive ? exitLabel : enterLabel}
      disabled={disabled}
      onClick={onClick}
    >
      {fullscreenActive ? <ScreenNormalIcon /> : <ScreenFullIcon />}
    </TooltipIconButton>
  ),
);

MediaFullscreenButton.displayName = "MediaFullscreenButton";
